import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import billingService from '../../services/BillingService.js';
import userService from '../../services/UserService.js';
import locationService from '../../services/LocationService.js';
import { FileText, ArrowLeft, Plus, Calendar } from "lucide-react";

// Maps frontend filter keys → API response keys
const SERVICE_TYPE_MAP = {
  'tank-cleaning': 'tank_cleanings',
  'dry-box':       'dry_boxes',
  'ti-report':     'ti_reports',
  'onhire':        'onhires',
  'ei-receipt':    'ei_receipts',
};

// Maps frontend filter keys → billing model ID-array field names
const SERVICE_IDS_FIELD_MAP = {
  'tank-cleaning': 'tank_cleaning_ids',
  'dry-box':       'dry_box_ids',
  'ti-report':     'ti_report_ids',
  'onhire':        'onhire_ids',
  'ei-receipt':    'ei_receipt_ids',
};

// Maps frontend filter keys → client rates field names
const SERVICE_RATE_MAP = {
  'tank-cleaning': 'tank_cleaning_rate',
  'dry-box':       'dry_box_rate',
  'ti-report':     'ti_report_rate',
  'onhire':        'onhire_rate',
  'ei-receipt':    'ei_receipt_rate',
};

const SERVICE_LABELS = {
  'tank-cleaning': 'Tank Cleaning',
  'dry-box':       'Dry Box',
  'ti-report':     'TI Report',
  'onhire':        'On Hire',
  'ei-receipt':    'EI Receipt',
};

const AddBilling = () => {
  const [formData, setFormData] = useState({
    client_id: '',
    location_id: '',
    billing_date: new Date().toISOString().split('T')[0],
    billing_type: 'SURVEY',
    description: (() => {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      const monthYear = d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
      return `Towards Survey and Inspection of Empty tank containers surveyed on behalf of line and issuing Cleanliness and Condition survey report during the month of ${monthYear}`;
    })(),
    sac_code: '998343',
    cgst: 0,
    sgst: 0,
    igst: 0,
    paid: 0,
    other_description: '',
    other_charges: 0,
    showOtherCharges: false,
    // Per-type selected IDs
    selectedServices: {
      'tank-cleaning': [],
      'dry-box':       [],
      'ti-report':     [],
      'onhire':        [],
      'ei-receipt':    [],
    },
  });

  const [clients, setClients]                 = useState([]);
  const [locations, setLocations]             = useState([]);
  // Flat array: each item has { ...serviceFields, service_type: 'tank-cleaning' }
  const [pendingServices, setPendingServices] = useState([]);
  // Rates returned by API: { tank_cleaning_rate, dry_box_rate, ... }
  const [clientRates, setClientRates]         = useState({});
  const [loading, setLoading]                 = useState(false);
  const [step, setStep]                       = useState(1);
  const [serviceFilter, setServiceFilter]     = useState('tank-cleaning');
  const [nextBillingId, setNextBillingId]     = useState('');
  const [billingIdInput, setBillingIdInput]   = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchLocations();
    fetchNextBillingId();
  }, []);

  // ─── Fetch helpers ──────────────────────────────────────────────────────────
  const fetchNextBillingId = async () => {
    try {
      const response = await billingService.getNextBillingId();
      const nextId = response.next_billing_id;
      setNextBillingId(nextId);
      setBillingIdInput(nextId);
    } catch (err) {
      console.error('Failed to fetch next billing id', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await userService.getClients();
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  // ─── Client change → load pending services ──────────────────────────────────
  const handleClientChange = async (userId) => {
    // Reset services when client changes
    setFormData(prev => ({
      ...prev,
      client_id: userId,
      selectedServices: {
        'tank-cleaning': [],
        'dry-box':       [],
        'ti-report':     [],
        'onhire':        [],
        'ei-receipt':    [],
      },
    }));
    setPendingServices([]);
    setClientRates({});

    if (!userId) return;

    try {
      const response = await billingService.getPendingServices(userId);
      const data = response || {};

      /*
        API returns:
        {
          tank_cleanings: [...],
          dry_boxes: [...],
          ti_reports: [...],
          onhires: [...],
          ei_receipts: [...],
          rates: { tank_cleaning_rate, dry_box_rate, ti_report_rate, onhire_rate, ei_receipt_rate },
          client: { ... }
        }

        Transform into a flat array so the UI can filter by service_type.
      */
      const onlyPublished = (records) => (records || []).filter((s) => s.published !== false);
      const flat = [
        ...onlyPublished(data.tank_cleanings).map(s => ({ ...s, service_type: 'tank-cleaning' })),
        ...onlyPublished(data.dry_boxes).map(s => ({ ...s, service_type: 'dry-box' })),
        ...onlyPublished(data.ti_reports).map(s => ({ ...s, service_type: 'ti-report' })),
        ...onlyPublished(data.onhires).map(s => ({ ...s, service_type: 'onhire' })),
        ...onlyPublished(data.ei_receipts).map(s => ({ ...s, service_type: 'ei-receipt' })),
      ];

      setPendingServices(flat);
      setClientRates(data.rates || {});

      // Auto-select all services by default
      const autoSelected = {
        'tank-cleaning': flat.filter(s => s.service_type === 'tank-cleaning').map(s => s.id),
        'dry-box':       flat.filter(s => s.service_type === 'dry-box').map(s => s.id),
        'ti-report':     flat.filter(s => s.service_type === 'ti-report').map(s => s.id),
        'onhire':        flat.filter(s => s.service_type === 'onhire').map(s => s.id),
        'ei-receipt':    flat.filter(s => s.service_type === 'ei-receipt').map(s => s.id),
      };
      setFormData(prev => ({ ...prev, selectedServices: autoSelected }));
      setClientRates(data.rates || {});
    } catch (err) {
      Alert.error('Failed to load pending services');
      console.error(err);
    }
  };

  // ─── Generic field change ────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // Auto-update description when billing_type changes
    if (name === 'billing_type') {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      const monthYear = d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });

      const descriptionMap = {
        SURVEY:     `Towards Survey and Inspection of Empty tank containers surveyed on behalf of line and issuing Cleanliness and Condition survey report during the month of ${monthYear}`,
        SERVICE:    `Towards service charges for the month of ${monthYear}`,
        INSPECTION: `Towards inspection charges for the month of ${monthYear}`,
        OTHER:      `Towards charges for the month of ${monthYear}`,
      };

      setFormData(prev => ({
        ...prev,
        billing_type: value,
        description: descriptionMap[value] || prev.description,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  // ─── Select All / Deselect All for a service type ────────────────────────────
  const handleSelectAllServices = (serviceType) => {
    const allIds = pendingServices
      .filter(s => s.service_type === serviceType)
      .map(s => s.id);
    const currentSelected = formData.selectedServices[serviceType] || [];
    const allSelected = allIds.length > 0 && allIds.every(id => currentSelected.includes(id));

    setFormData(prev => ({
      ...prev,
      selectedServices: {
        ...prev.selectedServices,
        [serviceType]: allSelected ? [] : allIds,
      },
    }));
  };

  // ─── Toggle service selection ────────────────────────────────────────────────
  const handleServiceSelect = (serviceId, serviceType) => {
    setFormData(prev => {
      const current = prev.selectedServices[serviceType] || [];
      const updated  = current.includes(serviceId)
        ? current.filter(id => id !== serviceId)
        : [...current, serviceId];
      return {
        ...prev,
        selectedServices: {
          ...prev.selectedServices,
          [serviceType]: updated,
        },
      };
    });
  };

  // ─── Derived totals ──────────────────────────────────────────────────────────
  const computedAmounts = (() => {
    let subtotal = 0;

    Object.entries(formData.selectedServices).forEach(([type, ids]) => {
      const rateKey = SERVICE_RATE_MAP[type];
      const rate    = clientRates[rateKey] || 0;
      subtotal += ids.length * rate;
    });

    const other = parseFloat(formData.other_charges) || 0;
    const taxableSubtotal = subtotal + other;
    const cgst_amount = Math.round(taxableSubtotal * (formData.cgst / 100));
    const sgst_amount = Math.round(taxableSubtotal * (formData.sgst / 100));
    const igst_amount = Math.round(taxableSubtotal * (formData.igst / 100));
    const total       = Math.round(taxableSubtotal + cgst_amount + sgst_amount + igst_amount);
    const balance     = total - (formData.paid || 0);

    return { subtotal, taxableSubtotal, cgst_amount, sgst_amount, igst_amount, total, balance };
  })();

  const totalSelectedCount = Object.values(formData.selectedServices)
    .reduce((acc, arr) => acc + arr.length, 0);

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      Alert.error('Please select a client');
      return;
    }
    if (totalSelectedCount === 0) {
      Alert.error('Please select at least one service');
      return;
    }
    if (computedAmounts.total <= 0) {
      Alert.error('Total amount must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      // Build per-type ID arrays matching the billing model field names
      const serviceIdsPayload = {};
      Object.entries(formData.selectedServices).forEach(([type, ids]) => {
        serviceIdsPayload[SERVICE_IDS_FIELD_MAP[type]] = ids;
      });

      const submitData = {
        client_id:    formData.client_id,
        location_id:  formData.location_id  || null,
        billing_date: formData.billing_date,
        billing_type: formData.billing_type,
        description:  formData.description  || null,
        sac_code:     formData.sac_code     || null,
        cgst:         formData.cgst,
        sgst:         formData.sgst,
        igst:         formData.igst,
        paid:         formData.paid || 0,
        billing_id:   billingIdInput.trim() || undefined,
        ...serviceIdsPayload,
        other_description: formData.other_description || '',
        other_charges: parseFloat(formData.other_charges) || 0,
      };

      await billingService.create(submitData);
      Alert.success('Billing created successfully!');
      navigate('/billings');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create billing');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

  const filteredServices = pendingServices.filter(s => s.service_type === serviceFilter);

  const selectedClient   = clients.find(c => c.user_id === formData.client_id);
  const selectedLocation = locations.find(l => l.id === parseInt(formData.location_id));

  // Build a human-readable label for each service item
  const getServiceLabel = (service) => {
    // Try common identifier fields; fall back to ID
    return (
      service.container_number ||
      service.job_number       ||
      service.tank_number      ||
      service.survey_number    ||
      `#${service.id}`
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />

        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">

                {/* Back Button */}
                <button
                  onClick={() => navigate('/billings')}
                  className="flex items-center gap-2 text-buttonBg hover:text-orange-600 font-medium mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Billings
                </button>

                {/* Step Indicator */}
                <div className="flex gap-4 mb-8 overflow-x-auto">
                  {[1, 2, 3, 4].map(stepNum => (
                    <div
                      key={stepNum}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                        step === stepNum
                          ? 'bg-buttonBg text-white'
                          : step > stepNum
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      <span className="font-semibold">{stepNum}</span>
                      <span className="hidden sm:inline">
                        {stepNum === 1 && 'Client'}
                        {stepNum === 2 && 'Services'}
                        {stepNum === 3 && 'Amounts'}
                        {stepNum === 4 && 'Review'}
                      </span>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">

                  {/* ── Step 1: Client & Details ─────────────────────────────────── */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <h2 className="text-base sm:text-xl font-semibold text-tertiary">Select Client</h2>
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
                          {/* Client */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Client*
                            </label>
                            <select
                              value={formData.client_id}
                              onChange={(e) => handleClientChange(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg text-base"
                            >
                              <option value="">-- Select a Client --</option>
                              {clients.map(client => (
                                // FIX: use user_id as value (not integer id)
                                <option key={client.user_id} value={client.user_id}>
                                  {client.name}
                                  {client.organization_name ? ` — ${client.organization_name}` : ''}
                                </option>
                              ))}
                            </select>
                            {formData.client_id && (
                              <p className="text-sm text-green-600 mt-2">✓ Client selected</p>
                            )}
                          </div>

                          {/* Location */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Location (Optional)
                            </label>
                            <select
                              name="location_id"
                              value={formData.location_id}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                            >
                              <option value="">-- Select a Location --</option>
                              {locations.map(location => (
                                // FIX: use location.city (not location.name which doesn't exist)
                                <option key={location.id} value={location.id}>
                                  {location.city}{location.state ? `, ${location.state}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Billing Date */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Billing Date*
                            </label>
                            <div className='relative w-full'>
                              <input
                                type="date"
                                name="billing_date"
                                value={formData.billing_date}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                              />
                              <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                            </div>
                          </div>

                          {/* Billing Type */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Billing Type*
                            </label>
                            <select
                              name="billing_type"
                              value={formData.billing_type}
                              onChange={handleChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                            >
                              <option value="SURVEY">Survey</option>
                              <option value="SERVICE">Service</option>
                              <option value="INSPECTION">Inspection</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>

                          {/* Description */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Description (Optional)
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              placeholder="Add any notes about this billing"
                              rows="3"
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg resize-none"
                            />
                          </div>

                          {/* Billing ID */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Billing ID
                            </label>
                            <input
                              type="text"
                              value={billingIdInput}
                              onChange={(e) => setBillingIdInput(e.target.value)}
                              placeholder="e.g. UMA-42"
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg font-mono"
                            />
                            {nextBillingId && billingIdInput !== nextBillingId && (
                              <p className="text-xs text-yellow-600 mt-1">
                                Auto-generated: {nextBillingId} — you've overridden it
                              </p>
                            )}
                          </div>

                          {/* SAC Code */}
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              SAC Code (Optional)
                            </label>
                            <input
                              type="text"
                              name="sac_code"
                              value={formData.sac_code}
                              onChange={handleChange}
                              placeholder="e.g. 998314"
                              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                            />
                          </div>
                        </div>
                        {!formData.client_id && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 w-full py-2">
                              <p className="text-sm text-yellow-800">Please select a client to proceed</p>
                            </div>
                          )}

                          {formData.client_id && pendingServices.length === 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 w-full py-2">
                              <p className="text-sm text-yellow-800">No pending services found for this client</p>
                            </div>
                          )}
                    </div>
                  )}

                  {/* ── Step 2: Service Selection ────────────────────────────────── */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <h2 className="text-xl font-bold text-gray-900">Select Services</h2>

                      {/* Service Type Filter Tabs */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Service Type
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(SERVICE_LABELS).map(([type, label]) => {
                            const count = (pendingServices.filter(s => s.service_type === type)).length;
                            const selectedCount = formData.selectedServices[type].length;
                            return (
                              <button
                                key={type}
                                type="button"
                                onClick={() => setServiceFilter(type)}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors relative ${
                                  serviceFilter === type
                                    ? 'bg-buttonBg text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                {label}
                                {/* Badge: shows available count */}
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                                  serviceFilter === type
                                    ? 'bg-white text-buttonBg'
                                    : 'bg-gray-400 text-white'
                                }`}>
                                  {selectedCount > 0 ? `${selectedCount}/` : ''}{count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Rate info for current type */}
                      {clientRates[SERVICE_RATE_MAP[serviceFilter]] > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-800">
                          Rate for {SERVICE_LABELS[serviceFilter]}:{' '}
                          <strong>{formatCurrency(clientRates[SERVICE_RATE_MAP[serviceFilter]])}</strong> per unit
                        </div>
                      )}

                      {/* Services List */}
                      {filteredServices.length > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {formData.selectedServices[serviceFilter].length} of {filteredServices.length} selected
                          </span>
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-orange-500"
                              checked={filteredServices.length > 0 && filteredServices.every(s => formData.selectedServices[serviceFilter].includes(s.id))}
                              onChange={() => handleSelectAllServices(serviceFilter)}
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {filteredServices.every(s => formData.selectedServices[serviceFilter].includes(s.id))
                                ? 'Deselect All'
                                : 'Select All'}
                            </span>
                          </label>
                        </div>
                      )}
                      <div className="border rounded-lg p-4 space-y-3 max-h-80 overflow-y-auto">
                        {filteredServices.length === 0 ? (
                          <p className="text-gray-600 py-4 text-center">
                            No {SERVICE_LABELS[serviceFilter]} services available for this client
                          </p>
                        ) : (
                          filteredServices.map(service => (
                            <label
                              key={`${service.service_type}-${service.id}`}
                              className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.selectedServices[service.service_type].includes(service.id)}
                                onChange={() => handleServiceSelect(service.id, service.service_type)}
                                className="w-5 h-5 mt-1 accent-orange-500"
                              />
                              <div className="flex-1">
                                {/* Top row: label + service_id badge */}
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <p className="font-semibold text-gray-900">
                                    {getServiceLabel(service)}
                                  </p>
                                  {service.service_id && (
                                    <span className="text-xs font-mono bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded">
                                      {service.service_id}
                                    </span>
                                  )}
                                </div>

                                {/* Detail grid */}
                                <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                                  {service.tank_name && (
                                    <>
                                      <span className="text-gray-400">Tank</span>
                                      <span className="text-gray-700 font-medium">{service.tank_name}</span>
                                    </>
                                  )}
                                  {service.container_number && (
                                    <>
                                      <span className="text-gray-400">Container</span>
                                      <span className="text-gray-700 font-medium">{service.container_number}</span>
                                    </>
                                  )}
                                  {service.inspected_by && (
                                    <>
                                      <span className="text-gray-400">Inspected by</span>
                                      <span className="text-gray-700">{service.inspected_by}</span>
                                    </>
                                  )}
                                  {service.location && (
                                    <>
                                      <span className="text-gray-400">Location</span>
                                      <span className="text-gray-700">{service.location}</span>
                                    </>
                                  )}
                                  {(service.survey_date || service.inspection_date || service.date) && (
                                    <>
                                      <span className="text-gray-400">Date</span>
                                      <span className="text-gray-700">
                                        {new Date(service.survey_date || service.inspection_date || service.date).toLocaleDateString('en-IN')}
                                      </span>
                                    </>
                                  )}
                                  {service.client_name && (
                                    <>
                                      <span className="text-gray-400">Client</span>
                                      <span className="text-gray-700">{service.client_name}</span>
                                    </>
                                  )}
                                </div>

                                {/* Footer: type tag + created date */}
                                {service.created_at && (
                                  <p className="text-xs text-gray-400 mt-1.5">
                                    {SERVICE_LABELS[service.service_type]} •{' '}
                                    {new Date(service.created_at).toLocaleDateString('en-IN')}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))
                        )}
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>{totalSelectedCount}</strong> service(s) selected across all types
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── Step 3: Tax & Payment Details ──────────────────────────── */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <h2 className="text-xl font-bold text-gray-900">Tax & Payment Details</h2>

                      {/* Service-wise breakdown */}
                      <div className="border rounded-lg divide-y">
                        {Object.entries(formData.selectedServices).map(([type, ids]) => {
                          if (ids.length === 0) return null;
                          const rate   = clientRates[SERVICE_RATE_MAP[type]] || 0;
                          const amount = ids.length * rate;
                          return (
                            <div key={type} className="flex justify-between items-center px-4 py-3 text-sm">
                              <span className="text-gray-700">
                                {SERVICE_LABELS[type]} × {ids.length} @ {formatCurrency(rate)}
                              </span>
                              <span className="font-semibold">{formatCurrency(amount)}</span>
                            </div>
                          );
                        })}
                        {totalSelectedCount === 0 && (
                          <p className="px-4 py-3 text-sm text-gray-500">No services selected</p>
                        )}
                      </div>

                      {/* Other Charges Toggle */}
                      <div className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Add Other Charges</span>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, showOtherCharges: !prev.showOtherCharges, other_charges: prev.showOtherCharges ? 0 : prev.other_charges, other_description: prev.showOtherCharges ? '' : prev.other_description }))}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.showOtherCharges ? 'bg-buttonBg' : 'bg-gray-300'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.showOtherCharges ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        {formData.showOtherCharges && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Other Description</label>
                              <input
                                type="text"
                                name="other_description"
                                value={formData.other_description}
                                onChange={handleChange}
                                placeholder="e.g. Handling charges"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Other Charges (₹)</label>
                              <input
                                type="number"
                                name="other_charges"
                                value={formData.other_charges}
                                onChange={handleChange}
                                onFocus={(e) => { if (parseFloat(e.target.value) === 0) setFormData(prev => ({ ...prev, other_charges: '' })); }}
                                onBlur={(e) => { if (e.target.value === '') setFormData(prev => ({ ...prev, other_charges: 0 })); }}
                                min="0"
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-buttonBg"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Auto-detect state code from client GST and show relevant tax fields */}
                      {(() => {
                        const stateCode = selectedClient?.gst_no?.substring(0, 2);
                        const isState27 = stateCode === '27';
                        return (
                          <div className="grid grid-cols-3 gap-4">
                            {selectedClient?.gst_no && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Client GST No
                                </label>
                                <div className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm font-mono text-gray-900">
                                  {selectedClient.gst_no}
                                </div>
                              </div>
                            )}
                            {isState27 ? (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">CGST (%)</label>
                                  <input
                                    type="number"
                                    name="cgst"
                                    value={formData.cgst}
                                    onChange={handleChange}
                                    onFocus={(e) => { if (parseFloat(e.target.value) === 0) setFormData(prev => ({ ...prev, cgst: '' })); }}
                                    onBlur={(e) => { if (e.target.value === '') setFormData(prev => ({ ...prev, cgst: 0 })); }}
                                    min="0" max="100"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-buttonBg"
                                    placeholder="e.g. 9"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">SGST (%)</label>
                                  <input
                                    type="number"
                                    name="sgst"
                                    value={formData.sgst}
                                    onChange={handleChange}
                                    onFocus={(e) => { if (parseFloat(e.target.value) === 0) setFormData(prev => ({ ...prev, sgst: '' })); }}
                                    onBlur={(e) => { if (e.target.value === '') setFormData(prev => ({ ...prev, sgst: 0 })); }}
                                    min="0" max="100"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-buttonBg"
                                    placeholder="e.g. 9"
                                  />
                                </div>
                              </>
                            ) : (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">IGST (%)</label>
                                <input
                                  type="number"
                                  name="igst"
                                  value={formData.igst}
                                  onChange={handleChange}
                                  onFocus={(e) => { if (parseFloat(e.target.value) === 0) setFormData(prev => ({ ...prev, igst: '' })); }}
                                  onBlur={(e) => { if (e.target.value === '') setFormData(prev => ({ ...prev, igst: 0 })); }}
                                  min="0" max="100"
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-buttonBg"
                                  placeholder="e.g. 18"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Paid Amount */}
                      {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Advance Paid (₹)
                        </label>
                        <input
                          type="number"
                          name="paid"
                          value={formData.paid}
                          onChange={handleChange}
                          onFocus={(e) => { if (parseFloat(e.target.value) === 0) setFormData(prev => ({ ...prev, paid: '' })); }}
                          onBlur={(e) => { if (e.target.value === '') setFormData(prev => ({ ...prev, paid: 0 })); }}
                          min="0"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-buttonBg"
                          placeholder="0.00"
                        />
                      </div> */}

                      {/* Summary */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Services Subtotal:</span>
                          <span className="font-semibold">{formatCurrency(computedAmounts.subtotal)}</span>
                        </div>
                        {(parseFloat(formData.other_charges) || 0) > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{formData.other_description || 'Other Charges'}:</span>
                            <span className="font-semibold">{formatCurrency(formData.other_charges)}</span>
                          </div>
                        )}
                        {formData.cgst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">CGST ({formData.cgst}%):</span>
                            <span className="font-semibold">{formatCurrency(computedAmounts.cgst_amount)}</span>
                          </div>
                        )}
                        {formData.sgst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">SGST ({formData.sgst}%):</span>
                            <span className="font-semibold">{formatCurrency(computedAmounts.sgst_amount)}</span>
                          </div>
                        )}
                        {formData.igst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">IGST ({formData.igst}%):</span>
                            <span className="font-semibold">{formatCurrency(computedAmounts.igst_amount)}</span>
                          </div>
                        )}
                        <div className="border-t pt-3 flex justify-between">
                          <span className="font-bold text-gray-900">Total:</span>
                          <span className="text-2xl font-bold text-buttonBg">{formatCurrency(computedAmounts.total)}</span>
                        </div>
                        {formData.paid > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Advance Paid:</span>
                            <span className="font-semibold text-green-600">- {formatCurrency(formData.paid)}</span>
                          </div>
                        )}
                        {formData.paid > 0 && (
                          <div className="flex justify-between text-sm border-t pt-2">
                            <span className="font-semibold text-gray-700">Balance Due:</span>
                            <span className="font-bold text-red-600">{formatCurrency(computedAmounts.balance)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Step 4: Review ───────────────────────────────────────────── */}
                  {step === 4 && (
                    <div className="space-y-6">
                      <h2 className="text-xl font-bold text-gray-900">Review Billing</h2>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

                        {/* LEFT COLUMN */}
                        <div className="space-y-4">

                          {/* Client Info */}
                          <div className="border rounded-lg p-4">
                          <h3 className="font-semibold text-gray-900 mb-3">Client Information</h3>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-gray-500">Client:</span>
                            {/* FIX: match by user_id (not id) */}
                            <span className="text-gray-900 font-medium">{selectedClient?.name || 'N/A'}</span>

                            {selectedClient?.organization_name && (
                              <>
                                <span className="text-gray-500">Organisation:</span>
                                <span className="text-gray-900">{selectedClient.organization_name}</span>
                              </>
                            )}

                            {selectedClient?.gst_no && (
                              <>
                                <span className="text-gray-500">GST No:</span>
                                <span className="text-gray-900">{selectedClient.gst_no}</span>
                              </>
                            )}

                            <span className="text-gray-500">Billing ID:</span>
                            <span className="text-gray-900 font-mono font-semibold">{billingIdInput || nextBillingId || '—'}</span>

                            <span className="text-gray-500">Billing Date:</span>
                            <span className="text-gray-900">{formData.billing_date}</span>

                            <span className="text-gray-500">Billing Type:</span>
                            <span className="text-gray-900">{formData.billing_type}</span>

                            {formData.location_id && (
                              <>
                                <span className="text-gray-500">Location:</span>
                                {/* FIX: use location.city / location.state */}
                                <span className="text-gray-900">
                                  {selectedLocation
                                    ? `${selectedLocation.city}${selectedLocation.state ? `, ${selectedLocation.state}` : ''}`
                                    : 'N/A'}
                                </span>
                              </>
                            )}

                            {formData.sac_code && (
                              <>
                                <span className="text-gray-500">SAC Code:</span>
                                <span className="text-gray-900">{formData.sac_code}</span>
                              </>
                            )}

                            {formData.description && (
                              <>
                                <span className="text-gray-500">Description:</span>
                                <span className="text-gray-900">{formData.description}</span>
                              </>
                            )}
                            {(parseFloat(formData.other_charges) || 0) > 0 && (
                              <>
                                <span className="text-gray-500">Other Description:</span>
                                <span className="text-gray-900">{formData.other_description || '—'}</span>
                                <span className="text-gray-500">Other Charges:</span>
                                <span className="text-gray-900 font-semibold">{formatCurrency(formData.other_charges)}</span>
                              </>
                            )}
                          </div>
                        </div>

                          {/* Selected Services breakdown */}
                          <div className="border rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 mb-3">
                              Selected Services ({totalSelectedCount})
                            </h3>
                            <div className="space-y-3">
                              {Object.entries(formData.selectedServices).map(([type, ids]) => {
                                if (ids.length === 0) return null;
                                const rate   = clientRates[SERVICE_RATE_MAP[type]] || 0;
                                const amount = ids.length * rate;
                                const items  = pendingServices.filter(
                                  s => s.service_type === type && ids.includes(s.id)
                                );
                                return (
                                  <div key={type} className="border-l-4 border-orange-400 pl-3">
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="font-medium text-gray-800 text-sm">
                                        {SERVICE_LABELS[type]} ({ids.length})
                                      </span>
                                      <span className="text-sm font-semibold text-buttonBg">
                                        {formatCurrency(amount)}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {items.map(s => (
                                        <span
                                          key={s.id}
                                          className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                                        >
                                          {getServiceLabel(s)}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                        </div>

                        {/* RIGHT COLUMN - Final Amounts */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Services Subtotal:</span>
                            <span className="font-semibold">{formatCurrency(computedAmounts.subtotal)}</span>
                          </div>
                          {(parseFloat(formData.other_charges) || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Other Charges:</span>
                              <span className="font-semibold">{formatCurrency(formData.other_charges)}</span>
                            </div>
                          )}
                          {formData.cgst > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">CGST ({formData.cgst}%):</span>
                              <span className="font-semibold">{formatCurrency(computedAmounts.cgst_amount)}</span>
                            </div>
                          )}
                          {formData.sgst > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">SGST ({formData.sgst}%):</span>
                              <span className="font-semibold">{formatCurrency(computedAmounts.sgst_amount)}</span>
                            </div>
                          )}
                          {formData.igst > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">IGST ({formData.igst}%):</span>
                              <span className="font-semibold">{formatCurrency(computedAmounts.igst_amount)}</span>
                            </div>
                          )}
                          <div className="border-t border-green-300 pt-3 flex justify-between">
                            <span className="font-bold text-gray-900">Total Amount:</span>
                            <span className="text-2xl font-bold text-green-600">
                              {formatCurrency(computedAmounts.total)}
                            </span>
                          </div>
                          {formData.paid > 0 && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Advance Paid:</span>
                                <span className="font-semibold text-green-700">
                                  - {formatCurrency(formData.paid)}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm border-t border-green-300 pt-2">
                                <span className="font-bold">Balance Due:</span>
                                <span className="font-bold text-red-600">
                                  {formatCurrency(computedAmounts.balance)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                      </div>
                    </div>
                  )}

                  {/* ── Navigation Buttons ──────────────────────────────────────── */}
                  <div className="flex gap-4 mt-8 pt-6 border-t">
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={() => setStep(step - 1)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                      >
                        Previous
                      </button>
                    )}

                    {step < 4 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (step === 1 && !formData.client_id) {
                            Alert.error('Please select a client');
                            return;
                          }
                          if (step === 1 && !billingIdInput.trim()) {
                            Alert.error('Billing ID cannot be empty');
                            return;
                          }
                          if (step === 2 && totalSelectedCount === 0) {
                            Alert.error('Please select at least one service');
                            return;
                          }
                          if (step === 2) {
                            const stateCode = selectedClient?.gst_no?.substring(0, 2);
                            if (stateCode === '27') {
                              setFormData(prev => ({ ...prev, cgst: 9, sgst: 9, igst: 0 }));
                            } else if (selectedClient?.gst_no) {
                              setFormData(prev => ({ ...prev, igst: 18, cgst: 0, sgst: 0 }));
                            }
                          }
                          if (step === 3 && computedAmounts.total <= 0) {
                            Alert.error('Total amount must be greater than 0');
                            return;
                          }
                          setStep(step + 1);
                        }}
                        className="px-6 py-2 bg-buttonBg text-white rounded-lg hover:bg-orange-600 font-medium ml-auto"
                      >
                        Next
                      </button>
                    )}

                    {step === 4 && (
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium ml-auto disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {loading ? (
                          <>
                            <span className="inline-block animate-spin">⏳</span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            Create Billing
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBilling;